import { useRef, useState } from "react";
import { Square, Circle, Triangle, Minus, ArrowRight, Star, Type, Trash2, RectangleHorizontal, BringToFront } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Slider } from "../ui/slider";

const W = 560;
const H = 320;
let uid = 0;

const starPts = (x, y, w, h) => Array.from({ length: 10 }, (_, i) => {
  const a = -Math.PI / 2 + (i * Math.PI) / 5;
  const r = i % 2 ? 0.4 : 1;
  return `${x + w / 2 + (Math.cos(a) * r * w) / 2},${y + h / 2 + (Math.sin(a) * r * h) / 2}`;
}).join(" ");

const Shape = ({ s }) => {
  const p = { fill: s.fill, stroke: s.stroke, strokeWidth: s.sw };
  const { x, y, w, h } = s;
  switch (s.type) {
    case "rect": return <rect x={x} y={y} width={w} height={h} {...p} />;
    case "round": return <rect x={x} y={y} width={w} height={h} rx={14} {...p} />;
    case "ellipse": return <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} {...p} />;
    case "triangle": return <polygon points={`${x + w / 2},${y} ${x + w},${y + h} ${x},${y + h}`} {...p} />;
    case "star": return <polygon points={starPts(x, y, w, h)} {...p} />;
    case "line": return <line x1={x} y1={y + h / 2} x2={x + w} y2={y + h / 2} stroke={s.stroke} strokeWidth={Math.max(2, s.sw)} />;
    case "arrow": return (
      <g stroke={s.stroke} strokeWidth={Math.max(2, s.sw)} fill={s.stroke}>
        <line x1={x} y1={y + h / 2} x2={x + w - 14} y2={y + h / 2} />
        <polygon points={`${x + w},${y + h / 2} ${x + w - 16},${y + h / 2 - 9} ${x + w - 16},${y + h / 2 + 9}`} strokeWidth={1} />
      </g>
    );
    case "text": return <text x={x} y={y + h * 0.75} fontSize={h * 0.8} fill={s.fill} fontFamily="Arial, sans-serif" fontWeight="600">{s.text}</text>;
    default: return null;
  }
};

const TOOLS = [["rect", Square, "Rectangle"], ["round", RectangleHorizontal, "Rounded"], ["ellipse", Circle, "Ellipse"], ["triangle", Triangle, "Triangle"], ["star", Star, "Star"], ["line", Minus, "Line"], ["arrow", ArrowRight, "Arrow"], ["text", Type, "Text"]];

export const ShapeComposer = ({ onInsert }) => {
  const [shapes, setShapes] = useState([]);
  const [sel, setSel] = useState(null);
  const drag = useRef(null);
  const svgRef = useRef();
  const cur = shapes.find((s) => s.id === sel);
  const upd = (patch) => setShapes((a) => a.map((s) => (s.id === sel ? { ...s, ...patch } : s)));

  const add = (type) => {
    const id = ++uid;
    const flat = type === "line" || type === "arrow";
    const s = { id, type, x: 180 + (shapes.length % 5) * 20, y: 90 + (shapes.length % 5) * 16, w: type === "text" ? 160 : 150, h: flat ? 24 : type === "text" ? 36 : 110, fill: type === "text" ? "#0F172A" : "#DBEAFE", stroke: "#0F6CBD", sw: 2, text: "Label" };
    setShapes((a) => [...a, s]);
    setSel(id);
  };
  const pt = (e) => { const r = svgRef.current.getBoundingClientRect(); return { x: ((e.clientX - r.left) * W) / r.width, y: ((e.clientY - r.top) * H) / r.height }; };
  const down = (e, s) => { e.stopPropagation(); setSel(s.id); const p = pt(e); drag.current = { id: s.id, dx: p.x - s.x, dy: p.y - s.y }; e.target.setPointerCapture?.(e.pointerId); };
  const move = (e) => { if (!drag.current) return; const p = pt(e); const { id, dx, dy } = drag.current; setShapes((a) => a.map((s) => (s.id === id ? { ...s, x: p.x - dx, y: p.y - dy } : s))); };

  const insert = async () => {
    const pad = 10;
    const xs = shapes.flatMap((s) => [s.x, s.x + s.w]);
    const ys = shapes.flatMap((s) => [s.y, s.y + s.h]);
    const x0 = Math.max(0, Math.min(...xs) - pad), y0 = Math.max(0, Math.min(...ys) - pad);
    const w = Math.min(W, Math.max(...xs) + pad) - x0, h = Math.min(H, Math.max(...ys) + pad) - y0;
    const clone = svgRef.current.cloneNode(true);
    clone.querySelectorAll("[data-ui]").forEach((n) => n.remove());
    clone.setAttribute("viewBox", `${x0} ${y0} ${w} ${h}`);
    clone.setAttribute("width", w);
    clone.setAttribute("height", h);
    const { rasterize } = await import("./ImageDialog");
    onInsert(await rasterize(new XMLSerializer().serializeToString(clone), w, h, 2));
  };

  return (
    <div className="space-y-3" data-testid="shape-composer">
      <div className="flex flex-wrap gap-1.5">
        {TOOLS.map(([t, I, l]) => (
          <Button key={t} variant="outline" size="sm" onClick={() => add(t)} data-testid={`shape-add-${t}`}><I className="h-4 w-4 mr-1" />{l}</Button>
        ))}
      </div>
      <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${W} ${H}`} className="w-full rounded-lg border bg-white touch-none"
        style={{ backgroundImage: "radial-gradient(#E2E8F0 1px, transparent 1px)", backgroundSize: "16px 16px" }}
        onPointerMove={move} onPointerUp={() => { drag.current = null; }} onPointerDown={() => setSel(null)} data-testid="shape-canvas">
        {shapes.map((s) => (
          <g key={s.id} onPointerDown={(e) => down(e, s)} style={{ cursor: "move" }}>
            <Shape s={s} />
            <rect x={s.x} y={s.y} width={s.w} height={s.h} fill="transparent" />
            {sel === s.id && <rect data-ui="1" x={s.x - 4} y={s.y - 4} width={s.w + 8} height={s.h + 8} fill="none" stroke="#0F6CBD" strokeDasharray="4 3" />}
          </g>
        ))}
        {!shapes.length && <text data-ui="1" x={W / 2} y={H / 2} textAnchor="middle" fill="#94A3B8" fontSize="14" fontFamily="sans-serif">Add shapes above, then drag them into place</text>}
      </svg>
      {cur ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-center text-xs" data-testid="shape-props">
          <label className="flex items-center gap-2">Fill<input type="color" value={cur.fill} onChange={(e) => upd({ fill: e.target.value })} data-testid="shape-fill" /></label>
          <label className="flex items-center gap-2">Outline<input type="color" value={cur.stroke} onChange={(e) => upd({ stroke: e.target.value })} data-testid="shape-stroke" /></label>
          <div className="flex items-center gap-2">Width<Slider min={30} max={400} value={[cur.w]} onValueChange={([v]) => upd({ w: v })} data-testid="shape-width" /></div>
          <div className="flex items-center gap-2">Height<Slider min={10} max={300} value={[cur.h]} onValueChange={([v]) => upd({ h: v })} data-testid="shape-height" /></div>
          {cur.type === "text" && <Input className="h-8 col-span-2" value={cur.text} onChange={(e) => upd({ text: e.target.value })} data-testid="shape-text-input" />}
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setShapes((a) => [...a.filter((s) => s.id !== sel), cur])} data-testid="shape-front"><BringToFront className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => { setShapes((a) => a.filter((s) => s.id !== sel)); setSel(null); }} data-testid="shape-delete"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
      ) : <p className="text-xs text-muted-foreground">Select a shape to change its colours and size.</p>}
      <div className="flex justify-end"><Button disabled={!shapes.length} onClick={insert} data-testid="shape-insert-btn">Insert drawing</Button></div>
    </div>
  );
};
